// Downloads a candidate's AI summary as a real .pdf file (replaces the old
// window.open + window.print() pattern which was blocked by pop-up blockers
// in production browsers). Reuses the shared generatePdfFromHtml helper that
// already powers the Marketing Brochure / Admin Manual / Employer Manual PDFs.

import { generatePdfFromHtml, generateMultiPagePdfFromHtml } from "@/lib/pdfUtil";

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const scoreColorFor = (s) => {
  if (s >= 80) return "#047857"; // emerald-700
  if (s >= 60) return "#1d4ed8"; // blue-700
  if (s >= 40) return "#b45309"; // amber-700
  return "#475569"; // slate-600
};

const STYLES = `
  .pdf-root { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Arial, sans-serif; color: #0f172a; background: #fff; }
  .pdf-root .page { padding: 40px 48px 56px; }
  .pdf-root .page + .page { page-break-before: always; }
  .pdf-root .cover { padding: 80px 48px 48px; }
  .pdf-root .cover .eyebrow { font-size: 13px; }
  .pdf-root .cover h1 { font-size: 36px; margin-bottom: 14px; }
  .pdf-root .cover .lede { font-size: 16px; color: #475569; max-width: 540px; line-height: 1.55; }
  .pdf-root .cover .toc { margin-top: 36px; border-top: 1px solid #e2e8f0; }
  .pdf-root .cover .toc-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; border-bottom: 1px solid #e2e8f0; gap: 16px; }
  .pdf-root .cover .toc-rank { font-size: 11px; font-weight: 700; letter-spacing: 0.22em; color: #94a3b8; width: 40px; }
  .pdf-root .cover .toc-name { font-size: 15px; font-weight: 600; flex: 1; min-width: 0; }
  .pdf-root .cover .toc-email { font-size: 12px; color: #64748b; margin-left: 8px; }
  .pdf-root .cover .toc-score { font-size: 15px; font-weight: 700; }
  .pdf-root .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #1d4ed8; }
  .pdf-root .brand-rule { height: 3px; width: 56px; margin: 8px 0 18px; background: linear-gradient(90deg, #1d4ed8 0%, #2563eb 35%, #38bdf8 100%); }
  .pdf-root h1 { font-size: 30px; line-height: 1.15; letter-spacing: -0.02em; margin: 0 0 6px; font-weight: 700; }
  .pdf-root .meta { font-size: 14px; color: #64748b; margin-bottom: 28px; }
  .pdf-root .score-card { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 22px; border: 1px solid #e2e8f0; margin-bottom: 32px; }
  .pdf-root .score-card .label { font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #64748b; }
  .pdf-root .score-card .value { font-size: 36px; font-weight: 700; line-height: 1; }
  .pdf-root .section { margin-top: 28px; page-break-inside: avoid; }
  .pdf-root .section h2 { font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #64748b; margin: 0 0 10px; }
  .pdf-root .summary { font-size: 15px; line-height: 1.55; color: #1e293b; }
  .pdf-root .skills { display: flex; flex-wrap: wrap; gap: 6px; }
  .pdf-root .skill { font-size: 12px; padding: 3px 8px; border: 1px solid #cbd5e1; background: #f8fafc; }
  .pdf-root .list { margin: 0; padding: 0; list-style: none; }
  .pdf-root .list li { font-size: 14px; line-height: 1.55; padding-left: 16px; position: relative; margin-bottom: 6px; }
  .pdf-root .list li::before { content: "·"; position: absolute; left: 4px; color: #94a3b8; }
  .pdf-root .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
  .pdf-root .strength h2 { color: #047857; }
  .pdf-root .gap h2 { color: #b45309; }
  .pdf-root .footer { margin-top: 48px; padding-top: 18px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; display: flex; justify-content: space-between; gap: 12px; }
  .pdf-root .rank-badge { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #1d4ed8; margin-right: 10px; }
`;

// Render a single candidate's section (used standalone and in bulk bundles).
function renderCandidatePage(resume, jobTitle, rank = null) {
  const name = resume.candidate_name || resume.filename || "Candidate";
  const email = resume.candidate_email || "";
  const score = Math.max(0, Math.min(100, Number(resume.score) || 0));
  const summary = resume.summary || "No AI summary generated yet.";
  const topSkills = Array.isArray(resume.top_skills) ? resume.top_skills : [];
  const strengths = Array.isArray(resume.strengths) ? resume.strengths : [];
  const gaps = Array.isArray(resume.gaps) ? resume.gaps : [];
  const filename = resume.filename || "—";
  const uploadedAt = resume.created_at
    ? new Date(resume.created_at).toLocaleString()
    : "";
  const scoreColor = scoreColorFor(score);

  return `
    <main class="page">
      <div class="eyebrow">
        ${rank ? `<span class="rank-badge">Rank #${rank}</span>` : ""}
        Hireloop PRO — Candidate Summary
      </div>
      <div class="brand-rule"></div>
      <h1>${esc(name)}</h1>
      <div class="meta">
        ${email ? esc(email) : ""}
        ${email && jobTitle ? " · " : ""}
        ${jobTitle ? `Role: <strong>${esc(jobTitle)}</strong>` : ""}
      </div>

      <div class="score-card" style="border-left: 4px solid ${scoreColor};">
        <div>
          <div class="label">AI Score</div>
          <div style="font-size: 13px; color: #475569; margin-top: 4px;">Against ${esc(jobTitle || "this role")}</div>
        </div>
        <div class="value" style="color: ${scoreColor};">${score}<span style="font-size:18px;color:#94a3b8;">/100</span></div>
      </div>

      <div class="section">
        <h2>AI Summary</h2>
        <p class="summary">${esc(summary)}</p>
      </div>

      <div class="section">
        <h2>Top Skills</h2>
        <div class="skills">
          ${
            topSkills.length
              ? topSkills.map((s) => `<span class="skill">${esc(s)}</span>`).join("")
              : '<span style="color:#94a3b8;font-size:13px;">No skills extracted</span>'
          }
        </div>
      </div>

      <div class="section two-col">
        <div class="strength">
          <h2>Strengths</h2>
          ${
            strengths.length
              ? `<ul class="list">${strengths.map((s) => `<li>${esc(s)}</li>`).join("")}</ul>`
              : '<div style="color:#94a3b8;font-size:13px;">None identified.</div>'
          }
        </div>
        <div class="gap">
          <h2>Gaps</h2>
          ${
            gaps.length
              ? `<ul class="list">${gaps.map((g) => `<li>${esc(g)}</li>`).join("")}</ul>`
              : '<div style="color:#94a3b8;font-size:13px;">None identified.</div>'
          }
        </div>
      </div>

      <div class="footer">
        <span>Source résumé: <strong>${esc(filename)}</strong>${uploadedAt ? ` · uploaded ${esc(uploadedAt)}` : ""}</span>
        <span>Generated by Hireloop PRO</span>
      </div>
    </main>
  `;
}

export async function printCandidateSummary(resume, jobTitle = "") {
  const name = resume.candidate_name || resume.filename || "Candidate";
  const safeName = (name || "candidate").replace(/[^a-zA-Z0-9-]+/g, "-").toLowerCase();
  await generatePdfFromHtml({
    filename: `Hireloop-Candidate-${safeName}.pdf`,
    styles: STYLES,
    body: renderCandidatePage(resume, jobTitle),
    overlayLabel: "Generating candidate PDF…",
  });
  return true;
}

// Build a single bundled PDF with a cover/TOC page followed by one page per
// candidate. Used by the "Print top N summaries" button for shortlist meetings.
export async function printTopCandidatesSummary(resumes, jobTitle = "") {
  if (!Array.isArray(resumes) || resumes.length === 0) return false;

  const safeRole = (jobTitle || "shortlist")
    .replace(/[^a-zA-Z0-9-]+/g, "-")
    .toLowerCase();
  const today = new Date().toISOString().slice(0, 10);

  const tocRows = resumes
    .map((r, idx) => {
      const score = Math.max(0, Math.min(100, Number(r.score) || 0));
      const name = r.candidate_name || r.filename || "Candidate";
      const email = r.candidate_email || "";
      return `
        <div class="toc-row">
          <div class="toc-rank">#${String(idx + 1).padStart(2, "0")}</div>
          <div class="toc-name">
            ${esc(name)}
            ${email ? `<span class="toc-email">· ${esc(email)}</span>` : ""}
          </div>
          <div class="toc-score" style="color: ${scoreColorFor(score)};">${score}/100</div>
        </div>
      `;
    })
    .join("");

  const cover = `
    <main class="page cover">
      <div class="eyebrow">Hireloop PRO — Shortlist Bundle</div>
      <div class="brand-rule"></div>
      <h1>Top ${resumes.length} ${resumes.length === 1 ? "candidate" : "candidates"}${jobTitle ? ` · ${esc(jobTitle)}` : ""}</h1>
      <div class="lede">
        AI-ranked shortlist generated ${esc(today)}. Each candidate's full
        summary — score, top skills, strengths and gaps — follows on its own
        page. Hand-off ready for hiring-manager review.
      </div>
      <div class="toc">${tocRows}</div>
    </main>
  `;

  const pages = resumes.map((r, idx) =>
    renderCandidatePage(r, jobTitle, idx + 1)
  );

  await generateMultiPagePdfFromHtml({
    filename: `Hireloop-Shortlist-${safeRole}-top${resumes.length}-${today}.pdf`,
    styles: STYLES,
    sections: [cover, ...pages],
    overlayLabel: `Bundling top ${resumes.length} candidates…`,
  });
  return true;
}
